import {
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    FormHelperText as MuiFormHelperText,
    Radio as MuiRadio,
    TextField as MuiTextField,
    Paper,
    Stack,
} from "@mui/material";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { Autocomplete, Checkbox, RadioGroup } from "formik-mui";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { projectApi } from "../../../../api/projectApi";
import { userApi } from "../../../../api/userApi";
import { OutlinedInput } from "../../../../components/form/input/OutlinedInput";
import { selectCurrentUser } from "../../../../store/global/authSlice";

function FormHelperText({ name, touched, errors, ...restProps }) {
    return (
        <MuiFormHelperText error={touched[name] && !!errors[name]} {...restProps}>
            <ErrorMessage name={name} />
        </MuiFormHelperText>
    );
}

function FlagshipProjectForm({ sx, ...restProps }) {
    const user = useSelector(selectCurrentUser);
    const formik = useFormikContext();
    const { touched, errors, isSubmitting } = formik;
    // groups list
    const [searchGroupTrigger, searchGroupResult] = userApi.endpoints.searchGroups.useLazyQuery({});
    const groupOwnerLoading = searchGroupResult.isLoading || searchGroupResult.isFetching;
    const groupOwnerOptions = searchGroupResult.data?.data ?? [];
    // project property
    const projectSizeResult = projectApi.endpoints.getProjectPropertySizes.useQuery();
    const projectDurationResult = projectApi.endpoints.getProjectPropertyDurations.useQuery();

    const updateGroupOwnerOptionFilter = (event, newInputValue) => {
        let filters = { userId: user?.id ?? "-" };
        if (Boolean(newInputValue)) {
            filters = { ...filters, name: newInputValue };
        }
        searchGroupTrigger(filters);
    };

    useEffect(() => {
        updateGroupOwnerOptionFilter(null, null);
    }, []);

    if (projectSizeResult.isLoading || projectDurationResult.isLoading) {
        return <div>loading...</div>;
    }

    return (
        <Stack spacing={3} sx={{ ...sx }} {...restProps}>
            <FormControl fullWidth>
                <FormLabel required>Owner</FormLabel>
                <Field
                    component={Autocomplete}
                    name="owner"
                    required={true}
                    loading={groupOwnerLoading}
                    options={groupOwnerOptions}
                    filterOptions={(option) => option}
                    getOptionLabel={(option) => (option?.name ? `[${option.id}] ${option.name}` : "")}
                    // defaultValue=""
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onInputChange={updateGroupOwnerOptionFilter}
                    renderInput={(params) => (
                        <MuiTextField {...params} type="text" name="owner" variant="outlined" placeholder="Owner" />
                    )}
                    renderOption={(props, option) => {
                        return (
                            <li {...props}>
                                <div>
                                    [#{option.id}] {option.name}
                                </div>
                                <div>
                                    (
                                    {option?.memberships
                                        ?.map((membership) => {
                                            return membership?.role?.name_display || membership?.role?.name || "?";
                                        })
                                        .join(",")}
                                    )
                                </div>
                            </li>
                        );
                    }}
                />
                <FormHelperText name="owner" touched={touched} errors={errors} />
            </FormControl>

            <FormControl fullWidth>
                <FormLabel required>Project name</FormLabel>
                <Field
                    component={OutlinedInput}
                    type="text"
                    name="project_name"
                    placeholder="Project name"
                    multiline
                    required={true}
                />
                <FormHelperText name="project_name" touched={touched} errors={errors} />
            </FormControl>

            <FormControl fullWidth>
                <FormLabel>Project name (en)</FormLabel>
                <Field
                    component={OutlinedInput}
                    type="text"
                    name="project_name_en"
                    placeholder="Project name (en)"
                    multiline
                />
                <FormHelperText name="project_name_en" touched={touched} errors={errors} />
            </FormControl>

            <FormControl>
                <FormLabel component="legend" required>
                    Size
                </FormLabel>
                <Field component={RadioGroup} name="size" required={true}>
                    {projectSizeResult.data?.data?.map((size) => (
                        <FormControlLabel
                            key={size.id}
                            control={<MuiRadio />}
                            value={size.id}
                            label={size.name_display ?? size.name}
                            // required={true}
                        />
                    ))}
                </Field>
                <FormHelperText name="size" touched={touched} errors={errors} />
            </FormControl>

            <FormControl>
                <FormLabel component="legend" required>
                    Duration
                </FormLabel>
                <Field component={RadioGroup} name="duration" required={true}>
                    {projectDurationResult.data?.data?.map((duration) => (
                        <FormControlLabel
                            key={duration.id}
                            control={<MuiRadio />}
                            value={duration.id}
                            label={duration.name_display ?? duration.name}
                            // required={true}
                        />
                    ))}
                </Field>
                <FormHelperText name="duration" touched={touched} errors={errors} />
            </FormControl>

            <FormControl>
                <FormLabel component="legend" required>
                    Project type
                </FormLabel>
                <FormGroup>
                    <FormControlLabel
                        control={<Field component={Checkbox} name="is_flagship" value={true} checked={true} />}
                        label="Flagship project."
                    />
                </FormGroup>
                <FormHelperText name="is_flagship" touched={touched} errors={errors} />
            </FormControl>
        </Stack>
    );
}

export default FlagshipProjectForm;
